import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveTab } from '../../redux/slices/appSlice';
import { useDesignV2 } from '../../utils/designV2';
import { playlistCoverUrl, DEFAULT_PLAYLIST_COVER } from '../../utils/cover';
import CoverArt from '../CoverArt';
import { useOpenFollowedArtist } from '../v2/YourArtistsShelf';
import { openPlaylist } from '../../utils/openInLibrary';
import { requestCreatePlaylist } from '../../utils/playlistOpenRequest';
import { track } from '../../utils/analytics';
import './LibraryTab.scss';

// Mobile web-only hub for "your stuff" — Likes, Favourite Artists,
// Playlists — plus Crate Digger which feeds off that same library
// signal. Spotify/SoundCloud both group these under a single Library
// tab on mobile; we do the same.
const LibraryTab = () => {
    const dispatch = useDispatch();
    const likesCount = useSelector((s) => s.likes?.likedTracks?.length || s.likes?.total || 0);
    const favsCount = useSelector((s) => s.followedArtists?.artists?.length || s.followedArtists?.total || 0);
    const playlistsCount = useSelector((s) => s.playlist?.playlists?.length || 0);
    const v2 = useDesignV2();
    // v2 phone tiles: the row's own art (Spotify Library) — faces of the
    // artists you follow, covers of your playlists. Up to 4 → a 2x2 mosaic.
    const artistFaces = useSelector((s) => (s.followedArtists?.artists || []).map((a) => a.artwork).filter(Boolean).slice(0, 4));
    const playlists = useSelector((s) => s.playlist?.playlists || []);
    const playlistCovers = playlists.map(playlistCoverUrl).filter((u) => u && u !== DEFAULT_PLAYLIST_COVER).slice(0, 4);
    const mosaic = (srcs, round) => (srcs.length === 0 ? null : (
        <span className={`library-tile-mosaic n${srcs.length >= 4 ? 4 : 1}${round ? ' is-round' : ''}`}>
            {(srcs.length >= 4 ? srcs : srcs.slice(0, 1)).map((src) => <CoverArt key={src} src={src} size={96} />)}
        </span>
    ));
    const tiles = {
        likes: <span className="library-tile-liked" aria-hidden="true"><svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg></span>,
        'followed-artists': mosaic(artistFaces, true),
        playlists: mosaic(playlistCovers, false),
    };

    const items = [
        {
            key: 'likes',
            label: 'Liked Tracks',
            desc: likesCount ? `${likesCount} track${likesCount === 1 ? '' : 's'}` : 'Tracks you like show up here',
            Icon: () => (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
            ),
        },
        {
            key: 'followed-artists',
            label: 'Favourite Artists',
            desc: favsCount ? `${favsCount} artist${favsCount === 1 ? '' : 's'}` : 'Follow DJs to track their mixes',
            Icon: () => (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <polygon points="18 12 19.8 15.5 23.5 16 21 18.5 21.5 22 18 20 14.5 22 15 18.5 12.5 16 16.2 15.5" />
                </svg>
            ),
        },
        {
            key: 'playlists',
            label: 'Playlists',
            desc: playlistsCount ? `${playlistsCount} playlist${playlistsCount === 1 ? '' : 's'}` : 'Your saved playlists',
            Icon: () => (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M15 6H3" /><path d="M15 12H3" /><path d="M15 18H3" />
                    <circle cx="19" cy="18" r="2" /><path d="M21 6V16" />
                </svg>
            ),
        },
        // Crate Digger lives in the bottom nav ("Digger") on mobile, so it's
        // intentionally not duplicated here in the Library hub.
    ];

    // DESIGN V2 (phone) — Spotify's Your Library: ONE list. Liked Tracks pinned,
    // then your playlists (square covers) and the artists you follow (round
    // faces) as rows of the same shape; two chips narrow it; + makes a playlist.
    // (Before: three summary rows plus a sideways shelf of big faces that
    // repeated "Favourite Artists" in another style — Erez 11-04.)
    const [filter, setFilterState] = useState(null); // null | 'playlists' | 'artists'
    const setFilter = (f) => { track('library_hub_filter_changed', { filter: f || 'all' }); setFilterState(f); };
    const followed = useSelector((s) => s.followedArtists?.artists || []);
    const openArtist = useOpenFollowedArtist('library_hub');
    if (v2) {
        const showPlaylists = filter !== 'artists';
        const showArtists = filter !== 'playlists';
        const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;
        const Row = ({ art, round, title, sub, onClick }) => (
            <li>
                <button type="button" className="v2-lib-row" onClick={onClick}>
                    <span className={`v2-lib-art${round ? ' is-round' : ''}`}>{art}</span>
                    <span className="v2-lib-text"><span className="v2-lib-name">{title}</span><span className="v2-lib-sub">{sub}</span></span>
                </button>
            </li>
        );
        const letter = (name) => <span className="v2-lib-letter">{(name || '?').charAt(0).toUpperCase()}</span>;
        const nothing = (!showPlaylists || playlists.length === 0) && (!showArtists || followed.length === 0);
        return (
            <div className="tab-container library-tab v2-library">
                <div className="lower-part">
                    <div className="v2-lib-head">
                        <h3 className="library-title">Library</h3>
                        <button type="button" className="v2-lib-add" aria-label="New playlist" title="New playlist"
                            onClick={() => { track('playlist_create_opened', { from: 'library_hub' }); requestCreatePlaylist(); dispatch(setActiveTab('playlists')); }}>
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        </button>
                    </div>
                    <div className="v2-lib-chips">
                        {[['playlists', 'Playlists'], ['artists', 'Artists']].map(([k, label]) => (
                            <button type="button" key={k} className={`v2-lib-chip${filter === k ? ' on' : ''}`} aria-pressed={filter === k} onClick={() => setFilter(filter === k ? null : k)}>{label}</button>
                        ))}
                    </div>
                    <ul className="v2-lib-list">
                        {showPlaylists && (
                            <Row art={tiles.likes} title="Liked Tracks" sub={likesCount ? `Playlist · ${plural(likesCount, 'track')}` : 'Tracks you like show up here'} onClick={() => dispatch(setActiveTab('likes'))} />
                        )}
                        {showPlaylists && showArtists && playlists.length > 0 && <li className="v2-lib-label">Playlists</li>}
                        {showPlaylists && playlists.map((p) => {
                            const cover = playlistCoverUrl(p);
                            const n = p.track_count ?? p.trackCount ?? (Array.isArray(p.tracks) ? p.tracks.length : null);
                            return (
                                <Row key={`p-${p.id}`} art={cover && cover !== DEFAULT_PLAYLIST_COVER ? <CoverArt src={cover} size={96} /> : letter(p.name)}
                                    title={p.name} sub={n != null ? `Playlist · ${plural(n, 'track')}` : 'Playlist'}
                                    onClick={() => { track('playlist_opened', { from: 'library_hub', layout: 'mobile' }); openPlaylist(dispatch, p.id); }} />
                            );
                        })}
                        {showArtists && followed.length > 0 && <li className="v2-lib-label">Artists</li>}
                        {showArtists && followed.map((a) => (
                            <Row key={`a-${a.artist_name}`} round art={a.artwork ? <CoverArt src={a.artwork} size={96} /> : letter(a.artist_name)}
                                title={a.artist_name} sub="Artist" onClick={() => openArtist(a)} />
                        ))}
                    </ul>
                    {nothing && (
                        <p className="v2-lib-empty">{filter === 'artists' ? 'Follow DJs and they show up here.' : 'Your playlists show up here.'}</p>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="tab-container library-tab">
            <div className="lower-part">
                <h3 className="library-title">Library</h3>
                <ul className="library-list">
                    {items.map(({ key, label, desc, Icon }) => (
                        <li key={key}>
                            <button className="library-item" onClick={() => dispatch(setActiveTab(key))}>
                                {v2 && tiles[key]
                                    ? <span className="library-item-tile">{tiles[key]}</span>
                                    : <span className="library-item-icon"><Icon /></span>}
                                <span className="library-item-text">
                                    <span className="library-item-label">{label}</span>
                                    <span className="library-item-desc">{desc}</span>
                                </span>
                                <svg className="library-item-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="9 18 15 12 9 6" />
                                </svg>
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

export default LibraryTab;
